import { useEffect, useImperativeHandle, forwardRef, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  getUserWatchlists,
  getWatchlistsSearchPage,
  getUserDefaultWatchlist,
  getWatchlistItems,
  setUserDefaultWatchlist,
  addStockToWatchlists,
  deleteWatchlistItem,
  createWatchlist,
  deleteWatchlists,
  reorderWatchlists,
  reorderWatchlistItems,
  renameWatchlist,
} from "../../../api/user";
import { cleanSymbol } from "../watchlistFormat";
import useLeagueContext from "../../../utils/useLeagueContext";
import { setWatchlistMobileScreen } from "../../../utils/watchlistMobileScreen";
import {
  NO_ELIGIBLE_ASSETS_MESSAGE,
  blocksUseInTrade,
  fetchEligibleCount,
  forgetEligibleCount,
} from "../../../utils/leagueListEligibility";
import MobileListPicker from "./MobileListPicker";
import MobileListDetail from "./MobileListDetail";
import MobileAddAssets from "./MobileAddAssets";
import ListSkeleton from "../../Skeleton/ListSkeleton";
import usePullToRefresh from "../../../hooks/usePullToRefresh";
import PullToRefreshIndicator from "../../Other/PullToRefreshIndicator";

const PAGE_SIZE = 3; // load 3-by-3 so the first rows appear fast, then auto-append

// Mobile (<1024) Watchlist. Flat picker home → list detail → add/edit.
// Navigation is internal state (Back returns to the picker), not routes.
const WatchlistMobile = forwardRef(function WatchlistMobile(props, ref) {
  const [userLists, setUserLists] = useState([]);
  const [curatedLists, setCuratedLists] = useState([]);
  const [activeListId, setActiveListId] = useState(null);

  // Client Rev 2 — the mobile half of the same rule the desktop card enforces:
  // a list with zero eligible assets cannot become the league's Trade list, and
  // says why. See utils/leagueListEligibility.
  const { slug: leagueSlug, game: leagueGameRaw, isMember } = useLeagueContext();
  const leagueGame = leagueSlug && isMember && leagueGameRaw ? leagueGameRaw : null;
  const [eligibleCount, setEligibleCount] = useState(undefined);
  const [eligibilityTick, setEligibilityTick] = useState(0);
  const [previews, setPreviews] = useState({});

  const [screen, setScreen] = useState("picker"); // "picker" | "detail" | "add"
  // The app header needs to know a list is open so it can draw a back arrow —
  // this screen has no route of its own to tell it. See
  // utils/watchlistMobileScreen for why, and for the client report behind it.
  useEffect(() => {
    setWatchlistMobileScreen(screen);
    return () => setWatchlistMobileScreen("picker");
  }, [screen]);
  const [selectedListId, setSelectedListId] = useState(null);
  const [editing, setEditing] = useState(false);

  const [items, setItems] = useState([]);
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsHasMore, setItemsHasMore] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [listsLoading, setListsLoading] = useState(true);
  const requestRef = useRef(0);
  const addedRef = useRef(false);
  const screenRef = useRef(screen);

  const buildPreview = (data) => {
    const its = Array.isArray(data?.items) ? data.items : [];
    return { symbols: its.slice(0, 3).map((it) => cleanSymbol(it.symbol)), count: data?.pagination?.total ?? its.length };
  };

  const fetchLists = async () => {
    const [uRes, cRes, dRes] = await Promise.all([
      getUserWatchlists(),
      getWatchlistsSearchPage(5),
      getUserDefaultWatchlist(),
    ]);
    const uLists = Array.isArray(uRes?.data?.watchlists) ? uRes.data.watchlists : [];
    const cLists = Array.isArray(cRes?.data?.watchlists) ? cRes.data.watchlists : [];
    setUserLists(uLists);
    setCuratedLists(cLists);
    setActiveListId(dRes?.data?.default_watchlist_id != null ? String(dRes.data.default_watchlist_id) : null);
    return [...uLists, ...cLists];
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setListsLoading(true);
      let all = [];
      try {
        // Load the list names first so the picker renders immediately.
        all = await fetchLists();
      } catch (e) {
        console.error("Failed to load watchlists", e);
      } finally {
        if (!cancelled) setListsLoading(false);
      }
      // Main content (the list names) is on screen — now backfill each list's
      // ticker preview ONE AT A TIME so it never bursts the network.
      for (const l of all) {
        if (cancelled) break;
        try {
          const r = await getWatchlistItems(l.id, 1, 3);
          if (cancelled) break;
          setPreviews((p) => ({ ...p, [l.id]: buildPreview(r?.data) }));
        } catch (e) {
          /* preview is best-effort */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- items for the open list ----
  const loadItems = async (id, page, append) => {
    const reqId = append ? requestRef.current : ++requestRef.current;
    if (append) setLoadingMore(true);
    else setItemsLoading(true);
    let more = false;
    try {
      const r = await getWatchlistItems(id, page, PAGE_SIZE);
      if (reqId !== requestRef.current) return;
      const its = Array.isArray(r?.data?.items) ? r.data.items : [];
      const hm = r?.data?.pagination?.has_more ?? its.length === PAGE_SIZE;
      setItems((prev) => (append ? [...prev, ...its] : its));
      setItemsPage(page);
      setItemsHasMore(append ? (its.length > 0 ? hm : false) : hm);
      if (!append) setPreviews((p) => ({ ...p, [id]: buildPreview(r?.data) }));
      more = hm && its.length > 0;
    } catch (e) {
      if (reqId === requestRef.current) console.error("Failed to load items", e);
    } finally {
      if (reqId === requestRef.current) {
        if (append) setLoadingMore(false);
        else setItemsLoading(false);
      }
    }
    // Auto-append the next 3 so the list fills progressively instead of blocking
    // on one big price-enriched request. Stops when there is nothing more.
    if (more && reqId === requestRef.current && !editing) {
      loadItems(id, page + 1, true);
    }
  };

  const openList = (id) => {
    // Push a history entry so the app header's back arrow (navigate(-1))
    // returns to the picker instead of leaving /search.
    window.history.pushState({ qushiWatchlist: "detail" }, "");
    setSelectedListId(id);
    setEditing(false);
    setScreen("detail");
    setItems([]);
    setItemsPage(1);
    setItemsHasMore(true);
    loadItems(id, 1, false);
  };

  useImperativeHandle(ref, () => ({
    selectListByName: (name) => {
      const list = curatedLists.find(
        (l) => l.name.toLowerCase().trim() === name.toLowerCase().trim()
      );
      if (list) {
        openList(list.id);
      }
    },
  }));

  const backToPicker = async () => {
    setScreen("picker");
    setEditing(false);
    setSelectedListId(null);
    try {
      await fetchLists();
    } catch (e) {
      console.error(e);
    }
  };

  const loadMore = () => {
    if (loadingMore || !itemsHasMore || selectedListId == null || editing) return;
    loadItems(selectedListId, itemsPage + 1, true);
  };

  // Track the current screen for the popstate handler.
  useEffect(() => {
    screenRef.current = screen;
  }, [screen]);

  // The app header's back arrow calls navigate(-1). When a list detail is open
  // (a history entry was pushed on open), that lands back here → return to the
  // picker instead of leaving /search.
  useEffect(() => {
    const onPop = () => {
      if (screenRef.current !== "picker") backToPicker();
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- derived ----
  const allLists = [...userLists, ...curatedLists];
  const selectedList = allLists.find((l) => l.id === selectedListId) || null;
  const isCurated = curatedLists.some((c) => c.id === selectedListId);
  const isActive = selectedList != null && String(activeListId) === String(selectedList.id);

  // ---- actions ----
  useEffect(() => {
    if (!leagueGame || selectedListId == null) {
      setEligibleCount(undefined);
      return undefined;
    }
    let cancelled = false;
    setEligibleCount(undefined);
    fetchEligibleCount(leagueGame, selectedListId).then((n) => {
      if (!cancelled) setEligibleCount(n);
    });
    return () => {
      cancelled = true;
    };
  }, [leagueGame, selectedListId, eligibilityTick]);

  const useInTradeBlocked = blocksUseInTrade(eligibleCount);

  const activateList = async (id) => {
    const targetId = id ?? selectedListId;
    if (targetId == null || String(activeListId) === String(targetId)) return;
    // Covers a tap that beats the count. Returning leaves the stored default —
    // and therefore the Trade wheel — exactly as it was.
    if (leagueGame) {
      const eligible = await fetchEligibleCount(leagueGame, targetId);
      if (blocksUseInTrade(eligible)) {
        if (String(targetId) === String(selectedListId)) setEligibleCount(0);
        toast.error("This list has no assets eligible for this league.");
        return;
      }
    }
    try {
      await setUserDefaultWatchlist({ default_watchlist_id: targetId });
      setActiveListId(String(targetId));
    } catch (e) {
      console.error(e);
      toast.error("Could not make this list active. Try again.");
    }
  };

  const addAsset = async (symbol) => {
    if (selectedListId == null || !symbol) return;
    try {
      const res = await addStockToWatchlists(symbol, [selectedListId]);
      // The count describes the list's contents, so it dies with them.
      forgetEligibleCount(leagueGame, selectedListId);
      setEligibilityTick((t) => t + 1);
      if (res?.status === 200) {
        addedRef.current = true;
        toast.success(`Added to ${selectedList?.name ?? "list"}`);
      } else {
        toast.error("Could not add the asset to this watchlist. Try again.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Could not add the asset to this watchlist. Try again.");
    }
  };

  const closeAdd = () => {
    setScreen("detail");
    if (addedRef.current) {
      addedRef.current = false;
      setItems([]);
      setItemsPage(1);
      setItemsHasMore(true);
      loadItems(selectedListId, 1, false);
    }
  };

  const removeAsset = async (item) => {
    try {
      await deleteWatchlistItem(item.id);
      // The count describes the list's contents, so it dies with them.
      forgetEligibleCount(leagueGame, selectedListId);
      setEligibilityTick((t) => t + 1);
      setItems((prev) => prev.filter((it) => it.id !== item.id));
      setPreviews((p) => {
        const cur = p[selectedListId];
        return cur ? { ...p, [selectedListId]: { ...cur, count: Math.max(0, (cur.count || 1) - 1) } } : p;
      });
    } catch (e) {
      console.error(e);
      toast.error("Could not remove the asset. Try again.");
    }
  };

  const reorderAssets = async (newItems) => {
    setItems(newItems); // optimistic
    try {
      await reorderWatchlistItems(selectedListId, newItems.map((it) => it.id));
    } catch (e) {
      console.error(e);
      toast.error("Could not save the watchlist order. Try again.");
    }
  };

  const reorderLists = async (orderedUserLists) => {
    setUserLists(orderedUserLists); // optimistic
    try {
      await reorderWatchlists(orderedUserLists.map((l) => l.id));
    } catch (e) {
      console.error(e);
      toast.error("Could not save the watchlist order. Try again.");
    }
  };

  const renameList = async (name) => {
    const trimmed = (name || "").trim();
    if (selectedListId == null || !trimmed) return;
    setUserLists((prev) => prev.map((l) => (l.id === selectedListId ? { ...l, name: trimmed } : l)));
    try {
      await renameWatchlist(selectedListId, trimmed);
    } catch (e) {
      console.error(e);
      toast.error("Could not rename the watchlist. Try again.");
    }
  };

  const createList = async (name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return false;
    try {
      await createWatchlist({ name: trimmed });
      toast.success("Watchlist created");
      await fetchLists();
      return true;
    } catch (e) {
      console.error(e);
      toast.error("Could not create the watchlist. Try again.");
      return false;
    }
  };

  const deleteList = async () => {
    if (selectedListId == null) return;
    try {
      const res = await deleteWatchlists([selectedListId]);
      if (res?.status === 200) {
        toast.success("List deleted");
        window.history.back(); // consume the pushed entry → popstate → picker
      } else {
        toast.error("Could not delete the watchlist. Try again.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Could not delete the watchlist. Try again.");
    }
  };

  const { containerProps, isRefreshing, pullDistance } = usePullToRefresh({
    onRefresh: fetchLists,
    disabled: screen !== "picker",
  });

  if (listsLoading) {
    return (
      <ListSkeleton
        rows={6}
        avatar={false}
        columns={1}
        columnWidths={[64]}
        className="px-4"
        rowClassName="py-4"
      />
    );
  }

  if (screen === "add") {
    return <MobileAddAssets listName={selectedList?.name} onClose={closeAdd} onAdd={addAsset} />;
  }

  if (screen === "detail") {
    return (
      <MobileListDetail
        list={selectedList}
        isCurated={isCurated}
        isActive={isActive}
        count={selectedListId != null ? previews[selectedListId]?.count ?? items.length : 0}
        items={items}
        itemsLoading={itemsLoading}
        loadingMore={loadingMore}
        hasMore={itemsHasMore}
        editing={editing}
        onLoadMore={loadMore}
        onEnterEdit={() => setEditing(true)}
        onExitEdit={() => setEditing(false)}
        onOpenAdd={() => setScreen("add")}
        onUseInTrade={() => activateList(selectedListId)}
        useInTradeBlocked={useInTradeBlocked}
        useInTradeBlockedMessage={NO_ELIGIBLE_ASSETS_MESSAGE}
        onRemove={removeAsset}
        onReorderAssets={reorderAssets}
        onRename={renameList}
        onDeleteList={deleteList}
      />
    );
  }

  return (
    <div {...containerProps}>
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
      <MobileListPicker
        userLists={userLists}
        curatedLists={curatedLists}
        previews={previews}
        activeListId={activeListId}
        onOpen={openList}
        onCreate={createList}
        onReorderLists={reorderLists}
      />
    </div>
  );
});

export default WatchlistMobile;
